import { useMemo } from 'react';

import { PreparedWorkload } from 'features/workload-optimization';

import { useIsCustomMetricsEnabled } from '../../../../_hooks/permissions/useIsCustomMetricsEnabled';

import { useIsPsiEnabled } from '../../../../_hooks/permissions/useIsPsiEnabled';
import { useDisabledResource } from '../../_hooks/useDisabledResource';
import { ExplanationTooltip } from '../ExplanationTooltip';
import { VpaSettingRow } from './_components/VpaSettingRow';
import { VpaSettingsContent } from './_components/VpaSettingsContent';
import { WorkloadVpaFormSettings, workloadVpaFormSettings } from './_constants';
import { useVpaSettingIsDefault } from './_hooks/useVpaSettingIsDefault';
import { NextVpaSettingsButton } from '../../../../_woop-settings/_components/layout/NextVpaSettingsButton';
import { SettingsLayout } from '../../../../_woop-settings/_components/layout/SettingsLayout';
import { ResourceProvider } from '../../../../_woop-settings/_components/Resource/ResourceProvider';
import {
  useVpaSelectedSettingName,
  VpaSelectedSettingNameProvider,
} from '../../../../_woop-settings/vpaSelectedSettingNameContexts';
import { RffControlsProvider } from '@cast/shared-rff-ui';

import { useAbility } from '@cast/shared-ability-data-access';

import { Box, Stack } from '@cast/design-system';

type Props = {
  workload: PreparedWorkload;
};

const VpaSettingsRows = () => {
  const { settingsList } = useVpaSelectedSettingName<WorkloadVpaFormSettings>();

  return (
    <>
      {settingsList.map((name) => (
        <VpaSettingRow key={name} name={name} />
      ))}
    </>
  );
};

export const VerticalRightsizingSettings = ({ workload }: Props) => {
  const isCustomMetricsEnabled = useIsCustomMetricsEnabled();
  const isPsiEnabled = useIsPsiEnabled();
  const { can } = useAbility();
  const isMemoryGrowthDetectionEnabled = can(
    'view',
    'FeatureWoopMemoryGrowthDetectionEnabled'
  );

  const settingsList = useMemo(() => {
    return workloadVpaFormSettings.filter((s) => {
      if (s === 'customMetrics' && !isCustomMetricsEnabled) return false;
      if (s === 'memoryGrowthDetection' && !isMemoryGrowthDetectionEnabled)
        return false;
      if (s === 'stallDetection' && !isPsiEnabled) return false;
      return true;
    });
  }, [isCustomMetricsEnabled, isPsiEnabled, isMemoryGrowthDetectionEnabled]);

  return (
    <Stack gap={8} data-testid="vertical-rightsizing-settings">
      <VpaSelectedSettingNameProvider
        defaultName="optimizedResources"
        useSettingIsDefault={useVpaSettingIsDefault}
        settingsList={settingsList}
      >
        <SettingsLayout
          settingsRows={<VpaSettingsRows />}
          settingsContent={
            <ResourceProvider useDisabledResource={useDisabledResource}>
              <RffControlsProvider namePrefix="verticalRightsizing">
                <RffControlsProvider namePrefix="policy">
                  <ExplanationTooltip skipAnnotationsTooltip followCursor>
                    <Box>
                      <VpaSettingsContent workload={workload} />
                    </Box>
                  </ExplanationTooltip>
                </RffControlsProvider>
              </RffControlsProvider>
            </ResourceProvider>
          }
          nextButton={<NextVpaSettingsButton />}
        />
      </VpaSelectedSettingNameProvider>
    </Stack>
  );
};
